import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../auth/AuthContext';
import config from '../../../config';
import content from '../../../content';
import { useFormConfig } from '../../../hooks/useFormConfig';
import {
  checkRegisterEmailExists,
  createRegisterAccount,
  triggerRegisterPasswordReset,
} from '../../../service/form';
import { ApiError } from '../../../utils/api-error';
import { authDebug, getAuthDebugError } from '../../../utils/auth-debug';
import { validateField } from '../../../utils/form-utils';
import ErrorSummary from '../../ErrorSummary/ErrorSummary';
import Notification from '../../Notification/Notification';
import { P } from '../../ui';
import ErrorMessage from '../ErrorMessage';
import Input from '../TextInput';
import type { LoginData, Props } from './types';

type RegisterStage = 'password' | 'emailOnly' | 'create';

const noAccountMessage =
  'No account has been found for this email. Click below to create a new account. If you have an account, please correct you email address above.';
const createdNotice = 'Account created, please check you inbox for your sign in details';
const existingNotice =
  'An account already exists for this email. Please sign in or use Forgot your password if you need to reset it.';
const expiredTemporaryPasswordNotice =
  'Your temporary password has expired. We\u2019ve sent a new temporary password to your email address. Check your inbox, then enter it below.';

const isExpiredTemporaryPasswordError = (error: unknown): boolean =>
  error instanceof Error &&
  error.name === 'NotAuthorizedException' &&
  error.message.toLowerCase().includes('temporary password has expired');

export default function SigninForm({ onSuccess, prefilledEmail, onRegisterNoticeChange }: Props) {
  const id = useId();
  const { signIn } = useAuthContext();
  const { portalComponent, tenant, formConfig } = useFormConfig();
  const registerEnabled = formConfig.register === true;

  const signInInFlightRef = useRef(false);
  const [disabled, setDisabled] = useState(false);
  const [beforeSignIn, setBeforeSignIn] = useState(!registerEnabled);
  const [registerStage, setRegisterStage] = useState<RegisterStage>('password');
  const [registerNotice, setRegisterNotice] = useState('');

  useEffect(() => {
    onRegisterNoticeChange?.(registerNotice);
  }, [onRegisterNoticeChange, registerNotice]);

  const createRegisterAccountMutation = useMutation({
    mutationFn: ({ email, currentTenant }: { email: string; currentTenant?: string }) =>
      createRegisterAccount(email, currentTenant),
  });
  const resetExpiredTemporaryPasswordMutation = useMutation({
    mutationFn: ({ email, currentTenant }: { email: string; currentTenant?: string }) =>
      triggerRegisterPasswordReset(email, currentTenant),
  });

  const {
    formState: { errors },
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    getValues,
    control,
  } = useForm<LoginData>({
    ...config.form,
    defaultValues: { username: prefilledEmail || '', password: '' },
  });

  const signInLink = tenant ? `/${tenant}/${config.urls.signin}` : `/${config.urls.signin}`;
  const resetPasswordLink = prefilledEmail
    ? `/${tenant}/${config.urls.resetPasswordEmail}?email=${encodeURIComponent(prefilledEmail)}`
    : `/${tenant}/${config.urls.resetPasswordEmail}`;
  const pageTitle = registerEnabled && registerStage !== 'password' ? 'Create an account' : 'Sign in';

  const normalizeEmail = (value: string): string => value.trim().toLowerCase();

  const resolveErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) {
      const detail = typeof error.detail === 'string' ? error.detail.trim() : '';
      return detail || error.message || content.errorApi;
    }

    if (error instanceof Error) {
      return error.message || content.errorApi;
    }

    return content.errorApi;
  };

  const submitSignIn = (data: LoginData): void => {
    if (signInInFlightRef.current) {
      return;
    }

    signInInFlightRef.current = true;
    const normalizedData = {
      username: normalizeEmail(data.username),
      password: data.password.trim(),
    };

    setDisabled(true);
    setRegisterNotice('');
    authDebug('signin.form.submit', {
      tenant,
      username: normalizedData.username,
    });
    signIn(normalizedData, {
      onSuccess: () => {
        authDebug('signin.form.success', {
          tenant,
          username: normalizedData.username,
        });
        onSuccess();
      },
      onError: async (err) => {
        authDebug('signin.form.error', {
          tenant,
          username: normalizedData.username,
          error: getAuthDebugError(err),
        });

        if (isExpiredTemporaryPasswordError(err)) {
          try {
            await resetExpiredTemporaryPasswordMutation.mutateAsync({
              email: normalizedData.username,
              currentTenant: tenant,
            });
            clearErrors('username');
            setValue('password', '');
            setRegisterNotice(expiredTemporaryPasswordNotice);
            return;
          } catch (resetError) {
            authDebug('signin.form.expired-password-reset.error', {
              tenant,
              username: normalizedData.username,
              error: getAuthDebugError(resetError),
            });
            setError('username', {
              message: resolveErrorMessage(resetError),
            });
          } finally {
            setDisabled(false);
          }
          return;
        }

        setError('username', {
          message: err.message,
        });
      },
      onSettled: (_data, error) => {
        signInInFlightRef.current = false;
        if (!isExpiredTemporaryPasswordError(error)) {
          setDisabled(false);
        }
      },
    });
  };

  const resolveRegisterEmail = async (email: string): Promise<boolean> => {
    authDebug('register.check.start', {
      tenant,
      email,
    });

    try {
      const exists = await checkRegisterEmailExists(email, tenant);
      authDebug('register.check.success', {
        tenant,
        email,
        exists,
      });
      setValue('username', email);
      setRegisterStage(exists ? 'password' : 'create');
      if (!exists) {
        setValue('password', '');
      }
      return exists;
    } catch (error) {
      authDebug('register.check.error', {
        tenant,
        email,
        error: getAuthDebugError(error),
      });
      throw error;
    }
  };

  const resetToBlankLogin = (): void => {
    setBeforeSignIn(!registerEnabled);
    setRegisterStage('password');
    setDisabled(false);
    setRegisterNotice('');
    clearErrors();
    setValue('username', '');
    setValue('password', '');
  };

  const resetToLoginStage = (): void => {
    setRegisterStage('password');
    setDisabled(false);
    clearErrors();
    setValue('password', '');
  };

  const returnToSignInWithNotice = (notice: string, email: string): void => {
    setRegisterNotice(notice);
    setRegisterStage('password');
    setDisabled(false);
    clearErrors();
    setValue('username', email);
    setValue('password', '');
  };

  const executeCreateOrReset = async (email: string): Promise<void> => {
    let returnedToSignIn = false;

    setDisabled(true);
    clearErrors('username');
    authDebug('register.create.start', {
      tenant,
      email,
    });

    try {
      const result = await createRegisterAccountMutation.mutateAsync({
        email,
        currentTenant: tenant,
      });
      authDebug('register.create.success', {
        tenant,
        email,
        status: result.status,
        created: result.created,
        exists: result.exists,
      });
      if (result.created) {
        returnToSignInWithNotice(createdNotice, result.email || email);
        returnedToSignIn = true;
        return;
      }

      if (result.exists || result.status === 'reset' || result.status === 'exists') {
        returnToSignInWithNotice(existingNotice, result.email || email);
        returnedToSignIn = true;
        return;
      }

      setError('username', {
        message: content.errorApi,
      });
    } catch (error) {
      authDebug('register.create.error', {
        tenant,
        email,
        error: getAuthDebugError(error),
      });
      setError('username', {
        message: resolveErrorMessage(error),
      });
    } finally {
      if (!returnedToSignIn) {
        setDisabled(false);
      }
    }
  };

  const onStartCreateAccountFlow = (): void => {
    authDebug('register.flow.create.start', {
      tenant,
    });
    clearErrors();
    setRegisterNotice('');
    setRegisterStage('emailOnly');
    setValue('password', '');
  };

  const onContinueCreateAccount = async (): Promise<void> => {
    const email = normalizeEmail(getValues('username'));
    const validationError = validateField('Email address', { required: true, email: true }, email);

    if (validationError) {
      setError('username', { message: validationError });
      return;
    }

    await executeCreateOrReset(email);
  };

  const onLoginSubmit = async (data: LoginData): Promise<void> => {
    if (!registerEnabled) {
      submitSignIn(data);
      return;
    }

    const email = normalizeEmail(data.username);
    let handedToSignIn = false;

    authDebug('signin.flow.start', {
      tenant,
      registerEnabled,
      registerStage,
      username: email,
    });
    setDisabled(true);
    clearErrors('username');
    setRegisterNotice('');

    try {
      const exists = await resolveRegisterEmail(email);
      if (!exists) {
        return;
      }

      handedToSignIn = true;
      submitSignIn({ ...data, username: email });
    } catch (error) {
      setError('username', {
        message: resolveErrorMessage(error),
      });
    } finally {
      if (!handedToSignIn) {
        setDisabled(false);
      }
    }
  };

  const onCreateAccount = async (data: LoginData): Promise<void> => {
    const email = normalizeEmail(data.username);
    await executeCreateOrReset(email);
  };

  if (beforeSignIn) {
    return (
      <>
        {portalComponent}
        <button
          type="submit"
          className="govuk-button"
          data-module="govuk-button"
          data-prevent-double-click="true"
          onClick={() => {
            setBeforeSignIn(false);
          }}
        >
          <b>Sign in</b>
          <svg
            className="govuk-button__start-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="17.5"
            height="19"
            viewBox="0 0 33 40"
            aria-hidden="true"
            focusable="false"
          >
            <path fill="currentColor" d="M0 0h13l20 20-20 20H0l20-20z" />
          </svg>
        </button>
      </>
    );
  }

  return (
    <>
      <ErrorSummary errors={errors} idPrefix={id} />
      {registerNotice && (
        <Notification
          content={registerNotice}
          className={
            registerNotice === createdNotice ? 'govuk-notification-banner--success' : undefined
          }
        />
      )}
      <h1 className="govuk-heading-xl">{pageTitle}</h1>

      {!registerEnabled && (
        <P>If this is your first time signing in, check your email for credentials to sign in.</P>
      )}

      <form
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await onLoginSubmit(data);
        })}
      >
        <div className={`govuk-form-group ${errors.username ? 'govuk-form-group--error' : ''}`}>
          <label htmlFor={`${id}username`} className="govuk-label">
            Email address
          </label>
          <Controller
            name="username"
            control={control}
            rules={{
              validate: (value: string) =>
                validateField('Email address', { required: true, email: true }, value),
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                {error && <ErrorMessage error={error} />}
                <Input
                  {...field}
                  id={`${id}username`}
                  type="email"
                  width="three-quarters"
                  autoComplete="off"
                  disabled={disabled}
                  onBlur={async (event) => {
                    field.onBlur();
                    if (!registerEnabled || registerStage === 'emailOnly') {
                      return;
                    }

                    const email = normalizeEmail(event.target.value || '');
                    if (!email) {
                      return;
                    }

                    try {
                      await resolveRegisterEmail(email);
                    } catch (error) {
                      setError('username', {
                        message: resolveErrorMessage(error),
                      });
                    }
                  }}
                  onChange={(event) => {
                    field.onChange(event);
                    if (registerEnabled && registerStage === 'create') {
                      setRegisterStage('password');
                      setValue('password', '');
                    }
                  }}
                />
              </>
            )}
          />
        </div>

        {registerEnabled && registerStage === 'emailOnly' && (
          <div className="govuk-button-group">
            <button
              type="button"
              className="govuk-button"
              data-module="govuk-button"
              data-prevent-double-click="true"
              disabled={disabled}
              onClick={() => {
                void onContinueCreateAccount();
              }}
            >
              Continue
            </button>
          </div>
        )}

        {(!registerEnabled || registerStage === 'password') && (
          <div className={`govuk-form-group ${errors.password ? 'govuk-form-group--error' : ''}`}>
            <label htmlFor={`${id}password`} className="govuk-label">
              Password
            </label>
            <Controller
              name="password"
              control={control}
              rules={{
                validate: (value: string) => validateField('Password', { required: true }, value),
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  {error && <ErrorMessage error={error} />}
                  <Input
                    {...field}
                    id={`${id}password`}
                    type="password"
                    width="three-quarters"
                    autoComplete="off"
                    disabled={disabled}
                  />
                </>
              )}
            />
          </div>
        )}

        {registerEnabled && registerStage === 'create' && <P>{noAccountMessage}</P>}

        {!registerEnabled && (
          <button
            type="submit"
            className="govuk-button"
            data-module="govuk-button"
            data-prevent-double-click="true"
            disabled={disabled}
          >
            {content.buttonSignin}
          </button>
        )}

        {registerEnabled && registerStage === 'password' && (
          <div className="govuk-button-group">
            <button
              type="submit"
              className="govuk-button"
              data-module="govuk-button"
              data-prevent-double-click="true"
              disabled={disabled}
            >
              {content.buttonSignin}
            </button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              data-module="govuk-button"
              data-prevent-double-click="true"
              disabled={disabled}
              onClick={onStartCreateAccountFlow}
            >
              Create an account
            </button>
          </div>
        )}

        {registerEnabled && registerStage === 'create' && (
          <div className="govuk-button-group">
            <button
              type="button"
              className="govuk-button"
              data-module="govuk-button"
              data-prevent-double-click="true"
              disabled={disabled}
              onClick={handleSubmit(onCreateAccount)}
            >
              Create an account
            </button>
          </div>
        )}
      </form>

      {(!registerEnabled || registerStage === 'password') && (
        <P>
          <Link to={resetPasswordLink} className="govuk-link">
            {content.forgotPassword}
          </Link>
        </P>
      )}

      {registerEnabled && registerStage !== 'password' && (
        <P>
          <Link
            to={signInLink}
            className="govuk-link"
            onClick={(event) => {
              event.preventDefault();
              resetToLoginStage();
            }}
          >
            Back to login
          </Link>
        </P>
      )}
    </>
  );
}


